import {
  deleteProductModule,
  getGeneralConfig,
  getProductModules,
  saveProductModule,
  type CampaignData,
} from '../../../services/ecommerceApi';
import { formatBannerDateRange } from './contentCards/bannerCardHelpers';
import { BANNER_PANEL_COLORS, DEFAULT_CONTENT_CARDS_SETTINGS, type ContentCardItem } from './contentCards/types';

const SOURCE_KEY = 'sourceCampaignId';
const GROUP_KEY = 'campaignPromoGroup';

const toYmd = (value?: string | null): string => {
  if (!value) return '';
  const raw = String(value).trim();
  const isoDay = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDay) return isoDay[1];
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toLocalYmd = (date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const isYmdExpired = (endDate?: string | null, now = new Date()): boolean => {
  const end = toYmd(endDate);
  return Boolean(end && toLocalYmd(now) > end);
};

const pruneExpiredCards = (cards: ContentCardItem[]): ContentCardItem[] =>
  cards.filter((card) => !isYmdExpired(card.endDate));

const isCampaignExpired = (campaign: CampaignData): boolean => isYmdExpired(campaign.endDate);

const hashIndex = (id: string, modulo: number): number => {
  let sum = 0;
  for (let i = 0; i < id.length; i++) sum += id.charCodeAt(i);
  return modulo > 0 ? sum % modulo : 0;
};

const campaignTypes = (campaign: CampaignData): string[] => {
  if (Array.isArray(campaign.types) && campaign.types.length > 0) return campaign.types;
  return campaign.type ? [campaign.type] : [];
};

const offerLabel = (campaign: CampaignData, isTr: boolean): string => {
  const types = campaignTypes(campaign);
  if (types.includes('BuyXPayY') && campaign.buyQuantity && campaign.payQuantity) {
    return isTr
      ? `${campaign.buyQuantity} Al ${campaign.payQuantity} Öde`
      : `Buy ${campaign.buyQuantity} Pay ${campaign.payQuantity}`;
  }
  if (types.includes('FreeShipping')) return isTr ? 'Ücretsiz Kargo' : 'Free Shipping';
  if (campaign.discountType === 'Percentage' && Number(campaign.discountValue) > 0) {
    return `%${campaign.discountValue}`;
  }
  if (campaign.discountType === 'Amount' && Number(campaign.discountValue) > 0) {
    return isTr ? `${campaign.discountValue} TL indirim` : `${campaign.discountValue} off`;
  }
  return '';
};

const campaignLinkUrl = (campaignId: string): string => `#/campaigns/${campaignId}`;

const parseModuleSettings = (settingsJson?: string | null): any => {
  if (!settingsJson) return null;
  try {
    return JSON.parse(settingsJson);
  } catch {
    return null;
  }
};

const campaignIdFromCard = (card: any): string => {
  const tagged = String(card?.[SOURCE_KEY] || card?.sourceCampaignId || '').trim();
  if (tagged) return tagged;
  const href = String(card?.linkUrl || '');
  const match = href.match(/#\/campaigns\/([^/?#]+)/i);
  return match?.[1] ? decodeURIComponent(match[1]) : '';
};

const isCampaignPromoModule = (parsed: any): boolean => {
  if (!parsed) return false;
  if (parsed[GROUP_KEY] === true) return true;
  if (parsed[SOURCE_KEY]) return true;
  return (Array.isArray(parsed.cards) ? parsed.cards : []).some((card: any) => Boolean(campaignIdFromCard(card)));
};

const tagCard = (card: ContentCardItem, campaignId: string): ContentCardItem => ({
  ...card,
  [SOURCE_KEY]: campaignId,
  linkUrl: campaignLinkUrl(campaignId),
  linkType: 'url',
} as ContentCardItem);

const collectPromoRows = async (companyId: string) => {
  const response = await getProductModules(companyId, 'content-cards');
  const rows = Array.isArray(response?.data) ? response.data : [];
  return rows
    .map((row: any) => ({ row, parsed: parseModuleSettings(row?.settingsJson) }))
    .filter((item) => isCampaignPromoModule(item.parsed));
};

const mergeCardsByCampaign = (items: Array<{ parsed: any }>): ContentCardItem[] => {
  const byId = new Map<string, ContentCardItem>();
  items.forEach(({ parsed }) => {
    const moduleCampaignId = String(parsed?.[SOURCE_KEY] || '').trim();
    (Array.isArray(parsed?.cards) ? parsed.cards : []).forEach((card: any) => {
      const id = campaignIdFromCard(card) || moduleCampaignId;
      if (!id) return;
      byId.set(id, tagCard({ ...(byId.get(id) || {}), ...card }, id));
    });
  });
  return Array.from(byId.values());
};

const resolveConfigId = async (companyId: string, configId?: string | null): Promise<string> => {
  if (configId) return configId;
  const res = await getGeneralConfig(companyId);
  return String(res?.data?.id || '');
};

const groupSettings = (parsed: any, isTr: boolean, cardCount: number) => {
  const columnsDesktop = cardCount <= 1 ? 2 : Math.min(3, Math.max(2, cardCount));
  return {
    ...DEFAULT_CONTENT_CARDS_SETTINGS,
    ...(parsed?.settings || {}),
    variant: 'banner',
    layoutTemplate: 'grid',
    columnsDesktop,
    columnsTablet: Math.min(2, columnsDesktop),
    columnsMobile: 1,
    gapPx: parsed?.settings?.gapPx ?? 16,
    cardRadiusPx: parsed?.settings?.cardRadiusPx ?? 18,
    showBullets: false,
    showDate: true,
    imageAspectRatio: parsed?.settings?.imageAspectRatio || '1/1',
    titleAlign: parsed?.settings?.titleAlign || 'left',
    gridAlign: parsed?.settings?.gridAlign || 'left',
    cardTitleStyles: parsed?.settings?.cardTitleStyles || {
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      fontSize: 20,
      color: '#ffffff',
      bold: true,
      italic: false,
    },
  };
};

const buildCard = (
  campaign: CampaignData,
  campaignId: string,
  isTr: boolean,
  existing?: ContentCardItem
): ContentCardItem => {
  const types = campaignTypes(campaign);
  const startDate = toYmd(campaign.startDate);
  const endDate = toYmd(campaign.endDate);
  const offer = offerLabel(campaign, isTr);
  const percentBadge =
    campaign.discountType === 'Percentage' && Number(campaign.discountValue) > 0
      ? String(Math.round(Number(campaign.discountValue)))
      : '';
  const defaultCta = types.includes('Coupon')
    ? (isTr ? 'Kuponu kullan' : 'Use coupon')
    : (isTr ? 'Alışverişe Başla' : 'Start shopping');

  return tagCard({
    ...(existing || {}),
    title: campaign.name,
    subtitle:
      offer ||
      (existing?.subtitle && existing.subtitle !== campaign.couponCode ? existing.subtitle : ''),
    description: String(campaign.description || '').trim() || existing?.description || defaultCta,
    badge: percentBadge || existing?.badge || '',
    startDate,
    endDate,
    date: formatBannerDateRange(startDate, endDate),
    ctaLabel: existing?.ctaLabel || defaultCta,
    panelColor: existing?.panelColor || BANNER_PANEL_COLORS[hashIndex(campaignId, BANNER_PANEL_COLORS.length)],
    imageSide: existing?.imageSide || 'left',
    align: existing?.align || 'left',
  }, campaignId);
};

const saveGroupModule = async (options: {
  companyId: string;
  configId: string;
  cards: ContentCardItem[];
  keepRow?: any;
  parsed?: any;
  isTr: boolean;
  enabled?: boolean;
}) => {
  const { companyId, configId, cards, keepRow, parsed, isTr } = options;
  const title = isTr ? 'Kampanyalar' : 'Campaigns';
  const enabled = options.enabled ?? (keepRow ? keepRow.isActive !== false && parsed?.enabled !== false : cards.length > 0);
  const settingsJson = JSON.stringify({
    enabled,
    [GROUP_KEY]: true,
    title,
    cards,
    settings: groupSettings(parsed, isTr, cards.length),
    position: {
      insertAfter: parsed?.position?.insertAfter || 'categories',
      targetPage: parsed?.position?.targetPage || 'homepage',
    },
  });

  if (cards.length === 0 && keepRow?.id) {
    await deleteProductModule({ id: keepRow.id, companyId });
    return null;
  }

  await saveProductModule({
    id: keepRow?.id || undefined,
    companyId,
    configId: keepRow?.configId || configId,
    moduleType: 'content-cards',
    tabName: keepRow?.tabName || 'İçerik Kartları',
    sectionTitle: title,
    displayOrder: keepRow ? Number(keepRow.displayOrder) || 0 : 0,
    isActive: enabled,
    settingsJson,
    selectedProductIds: JSON.stringify([]),
    selectedCategoryIds: JSON.stringify([]),
    maxCategories: 0,
    horizontalPosition: 50,
    itemsPerRow: Math.min(3, Math.max(2, cards.length || 2)),
  });
  return keepRow;
};

export const consolidateCampaignPromoCards = async (
  companyId: string,
  configId?: string | null,
  isTr = true
): Promise<void> => {
  const resolvedConfigId = await resolveConfigId(companyId, configId);
  if (!resolvedConfigId) return;
  const promoRows = await collectPromoRows(companyId);
  if (promoRows.length === 0) return;

  const cards = pruneExpiredCards(mergeCardsByCampaign(promoRows));
  const keep = promoRows.find((item) => item.parsed?.[GROUP_KEY] === true) || promoRows[0];
  const alreadyGrouped = promoRows.length <= 1 && promoRows[0]?.parsed?.[GROUP_KEY] === true;
  const storedCount = Array.isArray(keep?.parsed?.cards) ? keep.parsed.cards.length : 0;
  if (alreadyGrouped && storedCount === cards.length) return;

  await saveGroupModule({
    companyId,
    configId: resolvedConfigId,
    cards,
    keepRow: keep.row,
    parsed: keep.parsed,
    isTr,
  });

  await Promise.all(
    promoRows
      .filter((item) => item.row?.id && item.row.id !== keep.row?.id)
      .map((item) => deleteProductModule({ id: item.row.id, companyId }))
  );
};

export const syncCampaignPromoCard = async (options: {
  companyId: string;
  configId?: string | null;
  campaignId: string;
  campaign: CampaignData;
  isTr: boolean;
}): Promise<void> => {
  const { companyId, campaignId, campaign, isTr } = options;
  const configId = await resolveConfigId(companyId, options.configId);
  if (!configId) {
    console.warn('[CampaignPromoCard] configId yok, kart oluşturulamadı.');
    return;
  }

  await consolidateCampaignPromoCards(companyId, configId, isTr);
  const promoRows = await collectPromoRows(companyId);
  const keep = promoRows.find((item) => item.parsed?.[GROUP_KEY] === true) || promoRows[0];
  const cards = pruneExpiredCards(mergeCardsByCampaign(promoRows));
  const existing = cards.find((card) => campaignIdFromCard(card) === String(campaignId));
  const nextCard = buildCard(campaign, campaignId, isTr, existing);
  const hideCard = campaign.isActive === false || isCampaignExpired(campaign);
  const nextCards = hideCard
    ? cards.filter((card) => campaignIdFromCard(card) !== String(campaignId))
    : [...cards.filter((card) => campaignIdFromCard(card) !== String(campaignId)), nextCard];

  await saveGroupModule({
    companyId,
    configId,
    cards: nextCards,
    keepRow: keep?.row,
    parsed: keep?.parsed,
    isTr,
    enabled: nextCards.length > 0,
  });
};

export const setCampaignPromoCardEnabled = async (
  companyId: string,
  campaignId: string,
  enabled: boolean
): Promise<void> => {
  const promoRows = await collectPromoRows(companyId);
  if (promoRows.length === 0) return;
  const cards = mergeCardsByCampaign(promoRows);
  const nextCards = enabled
    ? cards
    : cards.filter((card) => campaignIdFromCard(card) !== String(campaignId));
  const keep = promoRows.find((item) => item.parsed?.[GROUP_KEY] === true) || promoRows[0];
  const configId = String(keep.row?.configId || '');
  await saveGroupModule({
    companyId,
    configId,
    cards: nextCards,
    keepRow: keep.row,
    parsed: keep.parsed,
    isTr: true,
    enabled: nextCards.length > 0,
  });
  await Promise.all(
    promoRows
      .filter((item) => item.row?.id && item.row.id !== keep.row?.id)
      .map((item) => deleteProductModule({ id: item.row.id, companyId }))
  );
};

export const removeCampaignPromoCard = async (companyId: string, campaignId: string): Promise<void> => {
  await setCampaignPromoCardEnabled(companyId, campaignId, false);
};
